import React from 'react';
import {
  listFiles,
  setFileSelection,
  FileMeta,
  readPendingFileSelection,
  writePendingFileSelection,
  notifyFilesChanged,
  FILES_CHANGED_EVENT,
} from './fileWorkspaceService';

// =============================================================================
// ContextStrip - "file apa saja yang sedang dipakai sebagai konteks" di dalam
// composer, supaya konteks terlihat DI CHAT UTAMA dan bukan hanya di panel File
// kanan (yang bisa tertutup). Dirender di atas textarea pada kedua composer
// (welcome screen + thread).
//
// Sumber data:
// - Percakapan aktif: GET /files?conversation_id -> baris dengan selected=true.
// - Chat baru (conversationId null): id pending dari localStorage
//   'pendingFileSelection' dipetakan ke nama via GET /files (library).
// Sinkron dua arah dengan panel File lewat event 'powerai:files-changed':
// strip refetch saat panel berubah, dan mendispatch event yang sama saat file
// dilepas dari sini.
// =============================================================================

interface ContextStripProps {
  conversationId: number | null;
  // Project files are always in AUTO context; show them on the strip too.
  projectId?: number | null;
  theme: 'light' | 'dark';
  themeColors: any;
}

const truncateName = (filename: string, maxLength: number = 22): string => {
  if (filename.length <= maxLength) return filename;
  const dot = filename.lastIndexOf('.');
  const ext = dot > 0 ? filename.slice(dot) : '';
  const stem = dot > 0 ? filename.slice(0, dot) : filename;
  const keep = maxLength - ext.length - 1;
  if (keep < 4) return filename.slice(0, Math.max(1, maxLength - 1)) + '…';
  return `${stem.slice(0, keep)}…${ext}`;
};

const ContextStrip: React.FC<ContextStripProps> = ({ conversationId, projectId = null, theme, themeColors }) => {
  const [files, setFiles] = React.useState<FileMeta[]>([]);
  const [lockedIds, setLockedIds] = React.useState<Set<number>>(() => new Set());
  // Generation counter: a stale fetch (conversation switched mid-flight) must
  // never overwrite the fresher list.
  const genRef = React.useRef(0);

  const refresh = React.useCallback(async () => {
    const gen = ++genRef.current;
    try {
      let projectRows: FileMeta[] = [];
      if (projectId != null) {
        try {
          projectRows = await listFiles(null, projectId);
        } catch {
          projectRows = [];
        }
      }
      if (conversationId != null) {
        const rows = await listFiles(conversationId);
        const selected = rows.filter((f) => f.selected);
        const seen = new Set(selected.map((f) => f.id));
        const extra = projectRows.filter((f) => !seen.has(f.id));
        if (genRef.current === gen) {
          setFiles([...selected, ...extra]);
          setLockedIds(new Set(projectRows.map((f) => f.id)));
        }
      } else {
        const pending = readPendingFileSelection();
        const rows = pending.length > 0 ? await listFiles(null) : [];
        const selected = rows.filter((f) => pending.includes(f.id));
        const seen = new Set(selected.map((f) => f.id));
        const extra = projectRows.filter((f) => !seen.has(f.id));
        if (genRef.current === gen) {
          setFiles([...selected, ...extra]);
          setLockedIds(new Set(projectRows.map((f) => f.id)));
        }
      }
    } catch {
      // Konteks strip bersifat informatif; kegagalan fetch tidak boleh berisik.
      if (genRef.current === gen) setFiles([]);
    }
  }, [conversationId, projectId]);

  React.useEffect(() => {
    refresh();
    const onChanged = () => { refresh(); };
    window.addEventListener(FILES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(FILES_CHANGED_EVENT, onChanged);
  }, [refresh]);

  const remove = async (file: FileMeta) => {
    // Optimistic: lepas dari strip dulu, panel menyusul via event.
    setFiles((prev) => prev.filter((f) => f.id !== file.id));
    try {
      if (conversationId != null) {
        await setFileSelection(file.id, conversationId, false);
      } else {
        writePendingFileSelection(readPendingFileSelection().filter((id) => id !== file.id));
      }
    } catch {
      /* kalau gagal, refetch di bawah mengembalikan keadaan sebenarnya */
    }
    notifyFilesChanged();
  };

  if (files.length === 0) return null;

  const c = themeColors[theme];

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px',
      marginBottom: '9px', paddingBottom: '9px',
      borderBottom: `1px solid ${c.line}`,
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        fontSize: '11px', color: c.ink3, fontFamily: c.mono,
        letterSpacing: '0.02em', marginRight: '2px',
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        KONTEKS · {files.length} FILE
      </span>
      {files.map((file) => (
        <div
          key={file.id}
          title={file.filename}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '2px 5px 2px 9px',
            background: c.panel2,
            border: `1px solid ${c.line}`,
            borderRadius: '999px', fontSize: '11.5px', color: c.ink2,
            maxWidth: '200px',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {truncateName(file.filename)}
          </span>
          {!lockedIds.has(file.id) && (
            <button
              onClick={() => remove(file)}
              aria-label={`Lepas ${file.filename} dari konteks`}
              title="Lepas dari konteks"
              style={{
                border: 'none', background: 'none', cursor: 'pointer',
                padding: '0 2px', color: c.ink3, lineHeight: 1, fontSize: '13px',
              }}
            >×</button>
          )}
        </div>
      ))}
    </div>
  );
};

export default ContextStrip;
